import type { LoadFromSessionOptions, ProjectInfo } from '@easterngraphics/wcf/modules/bsk';
import type { MainArticleElement } from '@easterngraphics/wcf/modules/cf';
import type { Application } from '@easterngraphics/wcf/modules/core';
import type { EaiwsContext } from '@easterngraphics/wcf/modules/eaiws';

import { CreationEvent, syncPricingProcedure } from '@egr/xbox/utils/Core';
import { getEnvBoolean } from '@egr/xbox/utils/ReactScriptHelper';
import { getPromiseCallbacks } from '@egr/xbox/utils/promise/Callbacks';

import { Basket } from '@easterngraphics/wcf/modules/bsk';
import { ArticleManager } from '@easterngraphics/wcf/modules/cf';
import { EaiwsSession, ProjectContext } from '@easterngraphics/wcf/modules/eaiws';
import { isNotNullOrEmpty, isNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

const GFJ_ENABLE_2D = getEnvBoolean('GFJ_ENABLE_2D', false);
const GFJ_ADD_INVISIBLE_LAYERS = getEnvBoolean('GFJ_ADD_INVISIBLE_LAYERS', false);

export class EaiwsBasket<T extends EaiwsContext = EaiwsContext> extends Basket {
    private _articleManager: ArticleManager | undefined;
    public onArticleManagerCreated = new CreationEvent<ArticleManager, EaiwsBasket<EaiwsContext>>(this);
    public get articleManager() {
        return this._articleManager;
    }

    /**
     * returns already created manager if exists
     * @param coreApp Application
     * @param callAfterCreation will be called before triggering the onArticleManagerCreated event
     * @returns ArticleManager
     */
    public async createArticleManager(
        coreApp: Application,
        callAfterCreation?: (articleManager: ArticleManager) => Promise<void>
    ): Promise<ArticleManager> {
        if (this._articleManager == null) {
            this._articleManager = new ArticleManager(coreApp, this.context);
            this._articleManager.setGfj2dGeometryEnabled(GFJ_ENABLE_2D);
            this._articleManager.setGfjInvisibleLayersEnabled(GFJ_ADD_INVISIBLE_LAYERS);

            await callAfterCreation?.(this._articleManager);
            this.onArticleManagerCreated.trigger(this._articleManager);
        } else {
            await callAfterCreation?.(this._articleManager);
        }

        return this._articleManager;
    }

    public override get context(): T {
        return super.context as T;
    }

    constructor(
        public gatekeeperId: string | undefined,
        session: T,
        projectInfo?: ProjectInfo
    ) {
        super(session, projectInfo);
    }
}

export class EaiwsBaskets {
    public static readonly basketManager = 'basketManager';
    public static readonly pConCatalog = 'pConCatalog';
    /** default basket */
    public basket: EaiwsBasket<EaiwsSession>;
    private customContext: Map<string, EaiwsContext> = new Map();
    private customBaskets: Map<string, EaiwsBasket<EaiwsContext>> = new Map();

    public hasBasket(type: string): boolean {
        return this.customBaskets.get(type) != null;
    }

    constructor(public session: EaiwsSession, gatekeeperId?: string, projectInfo?: ProjectInfo) {
        this.basket = new EaiwsBasket(gatekeeperId, session, projectInfo);
        this.getBasket = this.getBasket.bind(this);
        this.getContext = this.getContext.bind(this);
        this.createCustomBasket = this.createCustomBasket.bind(this);
    }

    public getContext(type: string): EaiwsContext {
        return this.customContext.get(type) ?? this.session;
    }

    public getBasket(type: string | undefined): EaiwsBasket<EaiwsContext> {
        if (isNullOrEmpty(type)) {
            return this.basket;
        }
        return this.customBaskets.get(type) ?? this.basket;
    }

    public getCustomBasket(type: string, gatekeeperId: string | undefined, context: EaiwsContext): EaiwsBasket<EaiwsContext>;
    public getCustomBasket(type: string, gatekeeperId: string | undefined): EaiwsBasket<EaiwsContext> | undefined;
    public getCustomBasket(type: string, gatekeeperId: string | undefined, context?: EaiwsContext): EaiwsBasket<EaiwsContext> | undefined {
        if (!this.customBaskets.has(type) && context) {
            return this.createCustomBasket(type, gatekeeperId, context);
        }
        return this.customBaskets.get(type);
    }

    public createCustomBasket(
        type: string,
        gatekeeperId: string | undefined,
        context: EaiwsContext,
        projectInfo?: ProjectInfo
    ) {
        this.customContext.set(type, context);
        const basket = new EaiwsBasket(gatekeeperId, context, projectInfo);
        this.customBaskets.set(type, basket);
        return basket;
    }

    public async createCustomBasketFromSessionId(
        sessionId: string,
        baseUrl: string,
        projectInfo?: ProjectInfo
    ) {
        const session = new EaiwsSession();
        const promiseCallbacks = getPromiseCallbacks();
        session.eventConnected.addListener(promiseCallbacks.resolve);
        session.connect(baseUrl, sessionId, null);

        await promiseCallbacks.promise;
        const context = new ProjectContext(session, sessionId);

        this.customContext.set(sessionId, context);

        const basket = new EaiwsBasket(undefined, context, projectInfo);
        this.customBaskets.set(sessionId, basket);
        return basket;
    }

    public removeCustomBasket(type: string) {
        this.customBaskets.get(type)?.clear();
        this.customBaskets.delete(type);
        this.customContext.delete(type);
    }

    public *sessions(): Iterable<EaiwsSession> {
        yield this.session;
        for (const context of this.customContext.values()) {
            if (context instanceof EaiwsSession) {
                yield context;
            }
        }
    }

    public *baskets(): Iterable<EaiwsBasket> {
        yield this.basket;
        for (const basket of this.customBaskets.values()) {
            yield basket;
        }
    }

    public async loadFromSession(options?: LoadFromSessionOptions, onlyDefault: boolean = false): Promise<void> {
        await this.basket.loadFromSession(options);
        if (!onlyDefault) {
            for (const basket of this.customBaskets.values()) {
                await basket.loadFromSession(options);
            }
        }
    }

    public async activatePricingProcedure(pricingProcedureName: string): Promise<void> {
        for (const basket of this.baskets()) {
            if (basket.calculation.activePricingProcedure == null) {
                try {
                    await basket.calculation.activatePricingProcedure(pricingProcedureName);
                } catch {
                    /* catch error if pricing not available */
                }
            }

            basket.onArticleManagerCreated.addListener(async (articleManager) => {
                await syncPricingProcedure(basket, articleManager);
            });
        }
    }

    public getBasketOfElement(element: MainArticleElement): EaiwsBasket<EaiwsContext> {
        if (this.customBaskets.size === 0) {
            return this.basket;
        }
        for (const basket of this.baskets()) {
            if (isNotNullOrEmpty(element.basketId) && basket.getItemById(element.basketId)) {
                return basket;
            }
        }
        return this.basket;
    }
}
